
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ errorInfo });
        // Log error to console for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReload = (): void => {
        window.location.reload();
    };

    handleGoHome = (): void => {
        window.location.href = '/';
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2] p-4">
                    <div className="bg-white p-10 rounded-[3rem] shadow-soft border border-sage-light text-center max-w-lg w-full">
                        <div className="size-20 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-4xl">error_outline</span>
                        </div>

                        <h2 className="text-2xl font-black text-earth mb-3">
                            Oops! Có lỗi xảy ra
                        </h2>

                        <p className="text-gray-500 font-body mb-6">
                            Ứng dụng gặp sự cố không mong muốn. Vui lòng thử tải lại trang hoặc quay về trang chủ.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mb-6 text-left bg-red-50 p-4 rounded-xl">
                                <summary className="cursor-pointer text-sm font-bold text-red-600 mb-2">
                                    Chi tiết lỗi (Dev Mode)
                                </summary>
                                <pre className="text-xs text-red-700 overflow-auto max-h-40 whitespace-pre-wrap">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={this.handleGoHome}
                                className="px-6 py-3 bg-sage-light text-earth rounded-xl font-bold hover:bg-gray-200 transition-all"
                            >
                                Về trang chủ
                            </button>
                            <button
                                onClick={this.handleReload}
                                className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-earth transition-all shadow-lg"
                            >
                                Tải lại trang
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
